(function () {
  const PREFETCH_PX = 500;

  /** True when the sentinel is in (or just below) the viewport — works even if scroll is on a nested container. */
  function isSentinelInLoadZone(sentinel) {
    if (!sentinel?.isConnected) return false;
    const rect = sentinel.getBoundingClientRect();
    const vh = window.innerHeight;
    const threshold = vh + PREFETCH_PX;
    return rect.top < threshold && rect.bottom > -PREFETCH_PX;
  }

  /** Walk up from the sentinel and collect elements that actually scroll (not the window). */
  function getScrollableAncestors(el) {
    const out = [];
    let p = el.parentElement;
    while (p && p !== document.body) {
      const st = window.getComputedStyle(p);
      const oy = st.overflowY;
      const scrollable =
        (oy === 'auto' || oy === 'scroll' || oy === 'overlay') && p.scrollHeight > p.clientHeight + 2;
      if (scrollable) out.push(p);
      p = p.parentElement;
    }
    return out;
  }

  let activeCleanup = null;

  function disconnectActive() {
    if (typeof activeCleanup === 'function') {
      activeCleanup();
      activeCleanup = null;
    }
  }

  function initCollectionInfiniteScroll() {
    disconnectActive();

    const root = document.getElementById('CollectionInfiniteScrollRoot');
    if (!root) return;

    const sentinel = root.querySelector('.collection-infinite-scroll__sentinel');
    const nextUrl = sentinel?.dataset?.nextUrl;
    if (!sentinel || !nextUrl) return;

    const liveGrid = document.getElementById('product-grid');
    const sectionId = liveGrid?.dataset?.id;
    if (!sectionId) return;

    const loader = root.querySelector('.collection-infinite-scroll__loader');
    let loading = false;

    let rafScrollScheduled = false;
    function scheduleProximityCheck() {
      if (rafScrollScheduled) return;
      rafScrollScheduled = true;
      requestAnimationFrame(() => {
        rafScrollScheduled = false;
        tryLoadNext();
      });
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !loading) tryLoadNext();
        });
      },
      {
        root: null,
        rootMargin: `0px 0px ${PREFETCH_PX}px 0px`,
        threshold: 0,
      }
    );

    function tryLoadNext() {
      if (loading) return;
      const urlToLoad = sentinel.dataset.nextUrl;
      if (!urlToLoad || !sentinel.isConnected) return;
      if (!isSentinelInLoadZone(sentinel)) return;

      loading = true;

      observer.unobserve(sentinel);
      loader?.classList.remove('hidden');

      const fetchUrl = new URL(urlToLoad, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);
      const fetchUrlString = fetchUrl.toString();

      fetch(fetchUrlString)
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fetchedGrid = doc.getElementById('product-grid');
          if (!fetchedGrid || !liveGrid) throw new Error('grid');

          const items = fetchedGrid.querySelectorAll(':scope > li.grid__item');
          items.forEach((li) => {
            const imported = document.importNode(li, true);
            imported.classList.add('scroll-trigger--cancel');
            liveGrid.appendChild(imported);
          });

          document.dispatchEvent(new CustomEvent('product-cards:updated'));

          const newRoot = doc.getElementById('CollectionInfiniteScrollRoot');
          const newSentinel = newRoot?.querySelector('.collection-infinite-scroll__sentinel');
          const upcoming = newSentinel?.dataset?.nextUrl;

          if (upcoming) {
            sentinel.dataset.nextUrl = upcoming;
          } else {
            observer.disconnect();
            root.remove();
            removeScrollListeners();
          }
        })
        .catch(() => {
          /* keep sentinel for retry */
        })
        .finally(() => {
          loading = false;
          loader?.classList.add('hidden');
          if (sentinel.isConnected) {
            observer.observe(sentinel);
            scheduleProximityCheck();
          }
        });
    }

    const scrollTargets = new Set([window, ...getScrollableAncestors(sentinel)]);

    function onScrollOrResize() {
      scheduleProximityCheck();
    }

    scrollTargets.forEach((t) => {
      t.addEventListener('scroll', onScrollOrResize, { passive: true });
    });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    function removeScrollListeners() {
      scrollTargets.forEach((t) => {
        t.removeEventListener('scroll', onScrollOrResize);
      });
      window.removeEventListener('resize', onScrollOrResize);
    }

    activeCleanup = () => {
      observer.disconnect();
      removeScrollListeners();
    };

    observer.observe(sentinel);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        tryLoadNext();
      });
    });
    window.addEventListener('load', scheduleProximityCheck, { once: true });
  }

  document.addEventListener('DOMContentLoaded', initCollectionInfiniteScroll);
  document.addEventListener('facet:product-grid-updated', initCollectionInfiniteScroll);
})();
